<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type { DataItem as SpecialOptionsConfirmStepNewAirRoadCustomTableDataItem } from '@/components/customTables/SpecialOptionsConfirmStepNewAirRoadCustomTable.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { ARServiceItem } from '@/models/airRoad';

import { computed } from 'vue';
import { format, toZonedTime } from 'date-fns-tz';
import _cloneDeep from 'lodash/cloneDeep';

import ProductsConfirmStepNewAirRoadCustomTable from '@/components/customTables/ProductsConfirmStepNewAirRoadCustomTable.vue';
import SpecialOptionsConfirmStepNewAirRoadCustomTable from '@/components/customTables/SpecialOptionsConfirmStepNewAirRoadCustomTable.vue';
import KSwitchField from '@/components/fields/KSwitchField.vue';
import H3LineNewAirRoadHeader from '@/components/headers/H3LineNewAirRoadHeader.vue';
import StepNewAirRoadHeader from '@/components/headers/StepNewAirRoadHeader.vue';
import PreviewDiagramNewAirRoadSection from '@/components/sections/PreviewDiagramNewAirRoadSection.vue';
import * as airRoadHelpers from '@/helpers/airRoad';
import * as productsConfirmStepNewAirRoadTableHelpers from '@/helpers/tables/productsConfirmStepNewAirRoad';
import * as specialOptionsConfirmStepNewAirRoadTableHelpers from '@/helpers/tables/specialOptionsConfirmStepNewAirRoad';
import { requiredValidationRule } from '@/helpers/validationRules';
import * as airRoadConstants from '@/constants/airRoad';

export interface Props {
  airRoadBookingJob?: ReadonlyDeep<openapi.AirroadJobDetailedContentOpenapi>;
}
const props = withDefaults(defineProps<Props>(), {
  airRoadBookingJob: undefined,
});

const model = defineModel<{
  params: openapi.AirroadSingleCreateQueryOpenapi;
  data: openapi.AirroadSingleCreateBodyOpenapi;
  _deliverySpecialOptions: Record<string, ARServiceItem>;
  _pickupSpecialOptions: Record<string, ARServiceItem>;
}>({
  default: () => _cloneDeep(airRoadConstants.defaultBookingState),
});

const productsConfirmStepNewAirRoadCustomTableData = computed(() => {
  return productsConfirmStepNewAirRoadTableHelpers.getTableData(
    model.value.data.items,
  );
});

const specialOptionsConfirmStepNewAirRoadCustomTableData = computed(() => {
  const stopTimeZone = airRoadHelpers.getStopTimeZone(
    props.airRoadBookingJob?.stops[model.value.data.ar_booked_leg - 1],
  );
  const zoneString = format(toZonedTime(new Date(), stopTimeZone), 'xxx', {
    timeZone: stopTimeZone,
  });
  const _specialOptionsConfirmStepNewAirRoadTableData: SpecialOptionsConfirmStepNewAirRoadCustomTableDataItem[] =
    [];
  let num = 0;
  Object.entries(model.value._deliverySpecialOptions).forEach(
    ([_, specialOption]) => {
      if (!specialOption.chosen) return;
      num = num + 1;
      _specialOptionsConfirmStepNewAirRoadTableData.push(
        specialOptionsConfirmStepNewAirRoadTableHelpers.getTableItem(
          specialOption,
          num,
          'Delivery',
          stopTimeZone,
          zoneString,
        ),
      );
    },
  );
  Object.entries(model.value._pickupSpecialOptions).forEach(
    ([_, specialOption]) => {
      if (!specialOption.chosen) return;
      num = num + 1;
      _specialOptionsConfirmStepNewAirRoadTableData.push(
        specialOptionsConfirmStepNewAirRoadTableHelpers.getTableItem(
          specialOption,
          num,
          'Pickup',
          stopTimeZone,
          zoneString,
        ),
      );
    },
  );
  return _specialOptionsConfirmStepNewAirRoadTableData;
});
</script>

<template>
  <section
    :class="$style.confirmStepNewAirRoadSection"
    data-testid="confirmStepNewAirRoadSection"
  >
    <StepNewAirRoadHeader>
      <h1>Job #{{ props.airRoadBookingJob?.rockhopper_id }}</h1>
      <template #right>
        <KSwitchField
          v-model="model.params.with_alloc"
          :class="$style.switchField"
          :rules="[requiredValidationRule]"
          hide-details
          inset
          is-label-color
          is-label-first
          label="Force allocation to AR (920) driver"
          priority="info"
        />
      </template>
    </StepNewAirRoadHeader>
    <PreviewDiagramNewAirRoadSection
      :ar-stops="model.data.ar_stops"
      :job="props.airRoadBookingJob"
    />
    <section class="w-50">
      <H3LineNewAirRoadHeader
        >Leg {{ model.data.ar_booked_leg }} products</H3LineNewAirRoadHeader
      >
      <ProductsConfirmStepNewAirRoadCustomTable
        :data="productsConfirmStepNewAirRoadCustomTableData"
      />
    </section>
    <section>
      <H3LineNewAirRoadHeader>Special options</H3LineNewAirRoadHeader>
      <SpecialOptionsConfirmStepNewAirRoadCustomTable
        :data="specialOptionsConfirmStepNewAirRoadCustomTableData"
      />
    </section>
  </section>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.confirmStepNewAirRoadSection {
}
  .switchField {
    margin-top: -4px;
  }
</style>
